/**
 * Scroll-driven animations: Web Animations whose progress is a scroll position, not the clock.
 *
 * With a native view timeline (Chrome 115, Safari 26) the compositor samples the animation on
 * every scrolled frame, in step with the scroll: no scroll event, no main-thread work, no lag on
 * a touch screen, where a scroll event reaches JavaScript after the frame that moved the page.
 * Without one (Firefox, older iOS) a driver scrubs the same animation: one passive scroll listener
 * schedules one frame, the frame reads each subject's box once and writes `currentTime` to every
 * animation of that subject. The time write is the only main-thread step; the effect composites.
 *
 * A `lag` adds inertia: the animation eases towards the scroll position instead of sitting on it,
 * so the effect keeps moving for a moment after the scroll stops (the numeric `scrub` of GSAP
 * ScrollTrigger, the damping of Lenis). A native timeline cannot lag, so a lag always takes the
 * driver, which then seeks every frame until the animations rest. The damping is exponential and
 * frame-rate independent: 60 Hz and 120 Hz screens draw the same path.
 *
 * Progress covers the subject's travel through the viewport: 0 when its top edge enters at the
 * bottom, 1 when its bottom edge leaves at the top (the `cover` range of a view timeline). The
 * viewport is the scroller. Keyframes must animate compositor properties only (transform,
 * translate, scale, rotate, opacity); anything else paints on every scrolled frame.
 *
 * The driver seeks its first frame after the page has painted. A subject already on screen then
 * would jump from its painted state to the scroll position: a layout shift to the eye. `anchor`
 * prevents it for keyframes whose middle is the rest state: the scroll position at the start
 * maps to that middle, and the target moves only once the user scrolls. An anchor always takes
 * the driver, and it does nothing for a subject that is off screen at the start.
 */

/**
 * The fallback maps progress 0..1 onto this many milliseconds of animation time. Any span works;
 * 1000 keeps the time readable as a permille in devtools.
 */
const FALLBACK_DURATION_MS = 1000;
/** A lagging animation rests once it is this close to the scroll position, in progress units (0.01% of the range). */
const REST_DELTA = 0.0001;
/** The frame length assumed for the first frame after rest, when no previous timestamp exists. */
const FIRST_FRAME_MS = 1000 / 60;
/** A target may sit outside its subject (a logo hanging over a banner's edge); the gate keeps driving until this far past the subject. */
const SUBJECT_MARGIN_PX = 240;

/**
 * @typedef {object} ScrollDrivenOptions
 * @property {Element} [subject] The element whose travel through the viewport is the progress. Defaults to the target.
 * @property {number} [lag] Time constant of the inertia in milliseconds: the animation closes about two thirds of the distance to the scroll position per `lag`, and rests after about four. 0 (the default) locks it to the scroll.
 * @property {boolean} [anchor] When true, a subject on screen at the start holds its painted state: that scroll position maps to the middle of the keyframes.
 */

/**
 * @typedef {object} DrivenAnimation One animation the driver seeks.
 * @property {number} lag Milliseconds, 0 for a lock.
 * @property {number} offset Added to the subject's progress before the seek; the anchor's shift, 0 without one.
 * @property {number} current Progress the animation sits at; -1 makes the next frame snap it to the scroll position.
 */

/**
 * @typedef {object} SubjectGroup Every driven animation of one subject, so its box is read once per frame.
 * @property {Element} subject The element whose box is the progress.
 * @property {Map<Animation, DrivenAnimation>} animations Every paused animation the frame seeks.
 * @property {boolean} visible Off-screen subjects skip the frame; the observer turns them back on.
 * @property {number} progress Scroll position of the subject at the last frame; -1 before the first.
 * @property {IntersectionObserver} observer The visibility gate of the subject.
 */

/** @type {WeakMap<Element, ViewTimeline>} */
const timelines = new WeakMap();
/** @type {Map<Element, SubjectGroup>} */
const groups = new Map();
/** @type {ResizeObserver|null} */
let resizeObserver = null;
/** @type {number|null} */
let frame = null;
/** @type {number|null} */
let previousTime = null;

/**
 * @returns {boolean}
 */
export function supportsViewTimeline() {
    return typeof ViewTimeline === 'function';
}

/**
 * @param {Element} target
 * @param {Array<Record<string, string|number>>} keyframes
 * @param {Element} subject
 * @returns {Animation}
 */
function animateOnViewTimeline(target, keyframes, subject) {
    let timeline = timelines.get(subject);
    if (!timeline) {
        timeline = new ViewTimeline({subject, axis: 'block'});
        timelines.set(subject, timeline);
    }

    return target.animate(keyframes, {timeline, fill: 'both'});
}

/**
 * Move one animation towards the scroll position of its subject.
 * @param {Animation} animation
 * @param {DrivenAnimation} driven
 * @param {number} target Progress of the subject.
 * @param {number} elapsed Milliseconds since the previous frame.
 * @returns {boolean} Whether the animation still has distance to close.
 */
function seek(animation, driven, target, elapsed) {
    let next = target;
    let moving = false;
    if (driven.lag > 0 && driven.current >= 0) {
        const distance = target - driven.current;
        moving = Math.abs(distance) > REST_DELTA;
        next = moving ? driven.current + distance * (1 - Math.exp(-elapsed / driven.lag)) : driven.current;
    }
    if (next !== driven.current) {
        driven.current = next;
        animation.currentTime = next * FALLBACK_DURATION_MS;
    }

    return moving;
}

/**
 * @param {number} progress
 * @returns {number}
 */
function clamp(progress) {
    return Math.max(0, Math.min(1, progress));
}

/**
 * @param {Element} subject
 * @returns {number} Where the subject is in its travel through the viewport, 0 to 1.
 */
function progressOf(subject) {
    const {top, height} = subject.getBoundingClientRect();

    return clamp((window.innerHeight - top) / (window.innerHeight + height));
}

/**
 * @param {number} time The frame timestamp.
 * @returns {void}
 */
function updateGroups(time) {
    frame = null;
    const elapsed = previousTime === null ? FIRST_FRAME_MS : time - previousTime;
    previousTime = time;
    let moving = false;
    for (const group of groups.values()) {
        if (!group.visible) {
            continue;
        }
        group.progress = progressOf(group.subject);
        for (const [animation, driven] of group.animations) {
            moving = seek(animation, driven, clamp(group.progress + driven.offset), elapsed) || moving;
        }
    }
    if (moving) {
        frame = requestAnimationFrame(updateGroups);
    } else {
        previousTime = null;
    }
}

/** @returns {void} */
function scheduleFrame() {
    if (frame === null && groups.size > 0) {
        frame = requestAnimationFrame(updateGroups);
    }
}

/**
 * The driver is page-wide and stays: its listeners do nothing while no group exists.
 * @returns {ResizeObserver}
 */
function getResizeObserver() {
    if (resizeObserver === null) {
        resizeObserver = new ResizeObserver(scheduleFrame);
        document.addEventListener('scroll', scheduleFrame, {passive: true, capture: true});
        window.addEventListener('resize', scheduleFrame, {passive: true});
    }

    return resizeObserver;
}

/**
 * @param {Element} subject
 * @returns {SubjectGroup}
 */
function getGroup(subject) {
    const existing = groups.get(subject);
    if (existing) {
        return existing;
    }

    /** @type {SubjectGroup} */
    const group = {
        subject,
        animations: new Map(),
        visible: true,
        progress: -1,
        observer: new IntersectionObserver(([entry]) => {
            group.visible = entry.isIntersecting;
            // A subject that comes back snaps to the scroll position: nothing glides in from where it left.
            for (const driven of group.animations.values()) {
                driven.current = -1;
            }
            scheduleFrame();
        }, {rootMargin: `${SUBJECT_MARGIN_PX}px 0px`}),
    };
    groups.set(subject, group);
    getResizeObserver().observe(subject);
    group.observer.observe(subject);

    return group;
}

/**
 * @param {SubjectGroup} group
 * @param {Animation} animation
 * @returns {void}
 */
function release(group, animation) {
    group.animations.delete(animation);
    if (group.animations.size > 0 || groups.get(group.subject) !== group) {
        return;
    }
    group.observer.disconnect();
    getResizeObserver().unobserve(group.subject);
    groups.delete(group.subject);
}

/**
 * @param {Element} target
 * @param {Array<Record<string, string|number>>} keyframes
 * @param {Element} subject
 * @param {number} lag
 * @param {boolean} anchor
 * @returns {Animation}
 */
function scrubOnScroll(target, keyframes, subject, lag, anchor) {
    const group = getGroup(subject);
    const animation = target.animate(keyframes, {duration: FALLBACK_DURATION_MS, fill: 'both'});
    animation.pause();
    const start = anchor ? progressOf(subject) : 0;
    /** @type {DrivenAnimation} */
    const driven = {lag, offset: start > 0 && start < 1 ? 0.5 - start : 0, current: -1};
    if (group.progress >= 0) {
        driven.current = clamp(group.progress + driven.offset);
        animation.currentTime = driven.current * FALLBACK_DURATION_MS;
    }
    group.animations.set(animation, driven);
    animation.addEventListener('cancel', () => release(group, animation), {once: true});
    scheduleFrame();

    return animation;
}

/**
 * Animate `target` through `keyframes` as `subject` travels through the viewport.
 *
 * Cancel the returned animation to stop driving the target and to drop its styles. Its time is
 * the scroll position, or trails it by `lag`: do not play, pause, or seek it.
 * @param {Element} target
 * @param {Array<Record<string, string|number>>} keyframes
 * @param {ScrollDrivenOptions} [options]
 * @returns {Animation}
 */
export function animateOnScroll(target, keyframes, {subject = target, lag = 0, anchor = false} = {}) {
    if (lag === 0 && !anchor && supportsViewTimeline()) {
        return animateOnViewTimeline(target, keyframes, subject);
    }

    return scrubOnScroll(target, keyframes, subject, lag, anchor);
}
